import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import * as Yup from 'yup';
import { useFormik } from 'formik';

import { _Object } from 'utils/interfaces';
import { capitalizeFirstLetter, closeModal, doAmountFormat, handleCartTotal, strings, printReceipt } from 'utils';
import { currenciesArray } from 'utils/currencies';
import { incrementQuantity, decrementQuantity, removeCartItem, addToCart, setCustomer } from 'redux/slices/cart.slice';

import { TextArea } from 'views/components';
import { Button, SearchField } from '../../../components/form-inputs'
import CustomerModal from './modal';
import { userService } from 'services';
import SearchAndModel from './search';
import TodaySalesSummary from './summary';
import CartDiscountModal from './cart-discount-modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCubesStacked,
  faXmark,
  faCheck,
  faPercent,
  faFileLines,
  faTrash,
  faPrint,
  faUser,
  faEllipsisVertical,
  faPause,
  faReceipt,
  faArrowLeft,
  faShoppingCart,
  faPlus,
} from '@fortawesome/free-solid-svg-icons';

const SalesTemplate1 = ({ orderFormik, btnLoading }: _Object) => {

  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { cart } = useSelector((state: _Object) => state.cart)
  const { me, printerDevice } = useSelector((state: _Object) => state.session)

  const [cartTotal, setCartTotal] = useState<_Object>({});

  // Check if a customer is selected
  const isCustomerSelected = Boolean(
    cart?.customer?.id ||
    cart?.customer?._id ||
    (cart?.customer && typeof cart?.customer === 'object' && (cart?.customer?.full_name || cart?.customer?.name || cart?.customer?.mobile_number || cart?.customer?.phone))
  );

  const isPlaceOrderDisabled = btnLoading || !cart?.lineItems?.length || !isCustomerSelected;
  const placeOrderButtonTitle = !isCustomerSelected
    ? 'Please select a customer first'
    : !cart?.lineItems?.length
      ? 'Cart is empty'
      : undefined;

  // Customer search step toggle
  const [isCustomerSearchOpen, setIsCustomerSearchOpen] = useState(false);
  const [customerSearchQuery, setCustomerSearchQuery] = useState('');
  const [customerSearchResults, setCustomerSearchResults] = useState<any[]>([]);
  const [isCustomerSearching, setIsCustomerSearching] = useState(false);
  const [selectedCustomerIndex, setSelectedCustomerIndex] = useState<number | null>(null);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isCustomerSearchOpen) {
        setIsCustomerSearchOpen(false);
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isCustomerSearchOpen]);

  const handleCustomerSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setCustomerSearchQuery(query);
    setSelectedCustomerIndex(null);
    if (!query.trim()) {
      setCustomerSearchResults([]);
      setIsCustomerSearching(false);
      return;
    }
    setIsCustomerSearching(true);
    userService
      .getCustomerSearch({ q: query.trim() })
      .then((res: any) => {
        setCustomerSearchResults(res?.items || []);
        setIsCustomerSearching(false);
      })
      .catch(() => {
        setIsCustomerSearching(false);
      });
  };

  const handleCustomerKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (customerSearchResults.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedCustomerIndex((prev) =>
          prev === null || prev === customerSearchResults.length - 1 ? 0 : prev + 1
        );
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedCustomerIndex((prev) =>
          prev === null || prev === 0 ? customerSearchResults.length - 1 : prev - 1
        );
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const indexToSelect = selectedCustomerIndex !== null ? selectedCustomerIndex : 0;
        if (customerSearchResults[indexToSelect]) {
          handleSelectCustomer(customerSearchResults[indexToSelect]);
        }
      } else if (e.key === 'Escape') {
        setIsCustomerSearchOpen(false);
      }
    }
  };

  const handleSelectCustomer = (customer: any) => {
    dispatch(setCustomer(customer));
    setCustomerSearchQuery('');
    setSelectedCustomerIndex(null);
    setIsCustomerSearchOpen(false);
  };

  // Payment panels state
  const [activePaymentMode, setActivePaymentMode] = useState<string>('cash');
  const [showNotePanel, setShowNotePanel] = useState<boolean>(false);
  const [discountValue, setDiscountValue] = useState<number | string>(0);
  const [activeDiscountPercent, setActiveDiscountPercent] = useState<number | null>(null);
  const [cashReceived, setCashReceived] = useState<number | string>('');
  const [cardType, setCardType] = useState<string>('Debit Card');
  const [cardRemark, setCardRemark] = useState<string>('');
  const [upiRemark, setUpiRemark] = useState<string>('');
  const [splitPayments] = useState<Array<{ id: string; method: string; amount: number | string }>>([
    { id: '1', method: 'Cash', amount: '' },
    { id: '2', method: 'UPI', amount: '' },
    { id: '3', method: 'Card', amount: '' }
  ]);

  const discountFormik = useFormik({
    initialValues: { label: '', amount: '' },
    validationSchema: Yup.object({
      amount: Yup.number()
        .label('Discount amount')
        .positive('Amount must be greater than zero')
        .required()

    }),
    enableReinitialize: true,
    onSubmit: () => {
      closeModal('discount-modal');
      discountFormik.resetForm();
    },
  });

  useEffect(() => {
    const response = handleCartTotal(cart, me);
    setCartTotal(response);
  }, [cart, me]);

  // Calculations for subtotal, tax, discount, total
  const subTotal = Number(cartTotal?.subTotal) || 0;
  const taxAmount = Number(cartTotal?.tax) || 0;
  const discountNum = Number(discountValue) || 0;
  const finalTotal = Math.max(0, subTotal + taxAmount - discountNum);
  const totalItemCount = cart?.lineItems?.reduce((acc: number, item: any) => acc + (Number(item?.quantity) || 1), 0) || 0;

  const currencySymbol = currenciesArray.find((item) => item.label === me?.client?.currency)?.value || process.env.REACT_APP_CURRENCY || '₹';
  const taxRatePercentage = subTotal > 0 && taxAmount > 0 
    ? Math.round((taxAmount / (subTotal - discountNum > 0 ? subTotal - discountNum : subTotal)) * 100) 
    : 0;

  const dynamicDenominations = (() => {
    if (finalTotal <= 0) return [200, 500, 1000];
    if (finalTotal <= 100) return [100, 200, 500];
    if (finalTotal <= 200) return [200, 500, 1000];
    if (finalTotal <= 500) return [500, 1000, 2000];
    if (finalTotal <= 1000) return [1000, 1500, 2000];
    const base = Math.ceil(finalTotal / 500) * 500;
    const next500 = base === finalTotal ? base + 500 : base;
    const next1000 = Math.ceil(finalTotal / 1000) * 1000;
    const higher = next1000 <= next500 ? next500 + 500 : next1000;
    return Array.from(new Set([next500, higher, higher + 500])).slice(0, 3);
  })();

  useEffect(() => {
    orderFormik.setFieldValue('amount', finalTotal);
    orderFormik.setFieldValue('discount', discountNum);
    orderFormik.setFieldValue('cash_received', cashReceived);
    if (activePaymentMode === 'card') {
      orderFormik.setFieldValue('card_type', cardType);
      orderFormik.setFieldValue('remark', cardRemark);
      orderFormik.setFieldValue('transaction_data', { card_type: cardType, remark: cardRemark });
    } else if (activePaymentMode === 'upi') {
      orderFormik.setFieldValue('remark', upiRemark);
      orderFormik.setFieldValue('transaction_data', { remark: upiRemark });
    } else if (activePaymentMode === 'split') {
      orderFormik.setFieldValue('split_payments', splitPayments);
      orderFormik.setFieldValue('transaction_data', { split_payments: splitPayments });
    } else {
      orderFormik.setFieldValue('transaction_data', { cash_received: cashReceived });
    }
  }, [finalTotal, discountNum, cashReceived, cardType, cardRemark, upiRemark, splitPayments, activePaymentMode]);

  useEffect(() => {
    if (!orderFormik.values.payment_mode) {
      orderFormik.setFieldValue('payment_mode', 'cash');
    }
  }, []);

  useEffect(() => {
    if (finalTotal > 0 && (cashReceived === '' || cashReceived === 0)) {
      setCashReceived(finalTotal);
    }
  }, [finalTotal]);

  const receivedNum = Number(cashReceived) || 0;
  const changeToReturn = Math.max(0, receivedNum - finalTotal);

  const handleSelectMode = (mode: string) => {
    setActivePaymentMode(mode);
    orderFormik.setFieldValue('payment_mode', mode);
    setShowNotePanel(false);
  };

  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  const [isHoldModalOpen, setIsHoldModalOpen] = useState(false);
  const moreMenuRef = useRef<HTMLDivElement>(null);
  const notePopoverRef = useRef<HTMLDivElement>(null);
  const [heldOrders, setHeldOrders] = useState<any[]>(() => {
    try {
      const saved = localStorage.getItem('pos_held_orders');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  // Close more menu & note popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(event.target as Node)) {
        setIsMoreMenuOpen(false);
      }
      if (notePopoverRef.current && !notePopoverRef.current.contains(event.target as Node)) {
        setShowNotePanel(false);
      }
    };
    if (isMoreMenuOpen || showNotePanel) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMoreMenuOpen, showNotePanel]);

  const handleNewSale = () => {
    setIsMoreMenuOpen(false);
    if (cart?.lineItems?.length > 0) {
      if (window.confirm('Start a new sale? Current cart items will be cleared.')) {
        dispatch(addToCart([]));
        dispatch(setCustomer({}));
        orderFormik.resetForm();
        setDiscountValue(0);
        setActiveDiscountPercent(null);
        orderFormik.setFieldValue('order_note', '');
        setCashReceived('');
        toast.info('New sale started');
      }
    } else {
      dispatch(addToCart([]));
      dispatch(setCustomer({}));
      orderFormik.resetForm();
      setDiscountValue(0);
      setActiveDiscountPercent(null);
      orderFormik.setFieldValue('order_note', '');
      setCashReceived('');
      toast.info('New sale ready');
    }
  };

  const handleHoldList = () => {
    setIsMoreMenuOpen(false);
    setIsHoldModalOpen(true);
  };

  const handleHoldCurrentOrder = () => {
    if (!cart?.lineItems?.length) {
      toast.warning('Cart is empty. Nothing to put on hold.');
      return;
    }
    const newHeldOrder = {
      id: Date.now(),
      customer: cart?.customer || null,
      customerName: cart?.customer?.full_name || cart?.customer?.name || `${cart?.customer?.first_name || ''} ${cart?.customer?.last_name || ''}`.trim() || 'Walk-in Customer',
      items: [...cart.lineItems],
      itemCount: totalItemCount,
      total: finalTotal,
      discount: discountValue,
      note: orderFormik.values.order_note || '',
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      date: new Date().toLocaleDateString()
    };
    const updated = [newHeldOrder, ...heldOrders];
    setHeldOrders(updated);
    localStorage.setItem('pos_held_orders', JSON.stringify(updated));
    dispatch(addToCart([]));
    dispatch(setCustomer({}));
    orderFormik.resetForm();
    setDiscountValue(0);
    setActiveDiscountPercent(null);
    orderFormik.setFieldValue('order_note', '');
    setCashReceived('');
    toast.success('Order placed on hold');
  };

  const handleResumeOrder = (heldOrder: any) => {
    if (cart?.lineItems?.length > 0) {
      if (!window.confirm('Current cart has items. Replace cart with this held order?')) {
        return;
      }
    }
    dispatch(addToCart(heldOrder.items || []));
    if (heldOrder.customer && heldOrder.customer.id) {
      dispatch(setCustomer(heldOrder.customer));
    } else {
      dispatch(setCustomer({}));
    }
    if (heldOrder.discount) setDiscountValue(heldOrder.discount);
    if (heldOrder.note) orderFormik.setFieldValue('order_note', heldOrder.note);

    const updated = heldOrders.filter((o) => o.id !== heldOrder.id);
    setHeldOrders(updated);
    localStorage.setItem('pos_held_orders', JSON.stringify(updated));
    toast.success('Held order resumed');
    setIsHoldModalOpen(false);
  };

  const handleDeleteHeldOrder = (orderId: number) => {
    const updated = heldOrders.filter((o) => o.id !== orderId);
    setHeldOrders(updated);
    localStorage.setItem('pos_held_orders', JSON.stringify(updated));
    toast.info('Held order removed');
  };

  const handleTodaySummary = () => {
    setIsMoreMenuOpen(false);
    setModal(true);
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'F1') {
        e.preventDefault();
        const searchInput = (document.getElementById('posProductSearchInput') ||
          document.querySelector('.search-input input[name="q"]') ||
          document.querySelector('input[name="q"]')) as HTMLInputElement | null;
        if (searchInput) {
          searchInput.focus();
          searchInput.select();
        }
      } else if (e.key === 'F2') {
        e.preventDefault();
      } else if (e.key === 'F3') {
        e.preventDefault();
      } else if (e.key === 'F4') {
        e.preventDefault();
        const discountInput = document.getElementById('posDiscountInput') as HTMLInputElement | null;
        if (discountInput) {
          discountInput.focus();
          discountInput.select();
        }
      } else if (e.key === 'F5') {
        e.preventDefault();
        if (cart?.lineItems?.length > 0) {
          if (window.confirm('Clear all items from cart?')) {
            dispatch(addToCart([]));
            toast.info('Cart cleared');
          }
        } else {
          toast.info('Cart is already empty');
        }
      } else if (e.key === 'F6') {
        e.preventDefault();
        if (cart?.lineItems?.length > 0) {
          handleHoldCurrentOrder();
        } else {
          setIsHoldModalOpen((prev) => !prev);
        }
      } else if (e.key === 'F7') {
        e.preventDefault();
        if (!isCustomerSelected) {
          toast.warning('Please select a customer before placing the order.');
          return;
        }
        if (!cart?.lineItems?.length || btnLoading) {
          return;
        }
        orderFormik.handleSubmit();
      } else if (e.key === 'F8') {
        e.preventDefault();
        const lastOrderStr = localStorage.getItem('pos_last_completed_order');
        if (lastOrderStr) {
          try {
            const lastOrder = JSON.parse(lastOrderStr);
            printReceipt(printerDevice, lastOrder, me);
            toast.info('Printing last completed order');
          } catch (err) {
            toast.error('Unable to print last order');
          }
        } else {
          toast.warning('No recent completed order found to print');
        }
      } else if (e.key === 'F9') {
        e.preventDefault();
        setIsCustomerSearchOpen(true);
        setTimeout(() => {
          const custInput = document.getElementById('customerSearchField') as HTMLInputElement | null;
          if (custInput) {
            custInput.focus();
            custInput.select();
          }
        }, 50);
      } else if (e.key === 'F10') {
        e.preventDefault();
        const addCustBtn = document.querySelector('[data-bs-target="#customer-modal-sales"]') as HTMLButtonElement | null;
        if (addCustBtn) {
          addCustBtn.click();
        }
      } else if ((e.ctrlKey || e.metaKey) && (e.key === 'n' || e.key === 'N')) {
        e.preventDefault();
        handleNewSale();
      } else if (e.key === 'Escape') {
        setIsMoreMenuOpen(false);
        setIsHoldModalOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [orderFormik, cart, discountValue, finalTotal, heldOrders, isCustomerSelected, btnLoading, printerDevice, me]);

  const renderMoreMenu = () => (
    <div className="pos-more-menu-container position-relative d-inline-flex flex-shrink-0" ref={moreMenuRef}>
      <button
        type="button"
        className={`pos-customer-more-btn border border-light-subtle rounded bg-white text-secondary d-flex align-items-center justify-content-center flex-shrink-0 ${isMoreMenuOpen ? 'active' : ''}`}
        onClick={() => setIsMoreMenuOpen(!isMoreMenuOpen)}
        title="More actions"
      >
        <FontAwesomeIcon icon={faEllipsisVertical} />
      </button>

      {isMoreMenuOpen && (
        <div className="pos-more-dropdown-menu position-absolute end-0 bg-white border border-light-subtle rounded shadow-sm p-1 d-flex flex-column gap-1">
          <button
            type="button"
            className="pos-dropdown-item d-flex align-items-center justify-content-between w-100 py-2 px-3 border-0 bg-transparent rounded text-start"
            onClick={handleNewSale}
          >
            <span className="pos-dropdown-item-left d-flex align-items-center gap-2">
              <FontAwesomeIcon icon={faFileLines} className="pos-item-icon text-dark" />
              <span className="pos-item-text text-dark fw-medium small">New Sale</span>
            </span>
            <span className="pos-item-shortcut text-muted small ms-3">Ctrl + N</span>
          </button>

          <button
            type="button"
            className="pos-dropdown-item d-flex align-items-center justify-content-between w-100 py-2 px-3 border-0 bg-transparent rounded text-start"
            onClick={handleHoldList}
          >
            <span className="pos-dropdown-item-left d-flex align-items-center gap-2">
              <FontAwesomeIcon icon={faPause} className="pos-item-icon text-dark" />
              <span className="pos-item-text text-dark fw-medium small">Hold List</span>
            </span>
            <span className="pos-item-shortcut text-muted small ms-3">F6</span>
          </button>

          <button
            type="button"
            className="pos-dropdown-item d-flex align-items-center justify-content-between w-100 py-2 px-3 border-0 bg-transparent rounded text-start"
            onClick={handleTodaySummary}
          >
            <span className="pos-dropdown-item-left d-flex align-items-center gap-2">
              <FontAwesomeIcon icon={faReceipt} className="pos-item-icon text-dark" />
              <span className="pos-item-text text-dark fw-medium small">Today Summary</span>
            </span>
          </button>
        </div>
      )}
    </div>
  );

  const [modal, setModal] = useState(false);
  const [mobileActiveView, setMobileActiveView] = useState<'catalog' | 'checkout'>('catalog');

  useEffect(() => {
    if (!cart?.lineItems?.length && mobileActiveView === 'checkout') {
      setMobileActiveView('catalog');
    }
  }, [cart?.lineItems?.length]);

  return (
    <>
      <div className="pos-sales-layout-wrapper d-flex flex-row w-100 vh-100 overflow-hidden">
        <div className={`pos-sales-catalog-section sales-list flex-grow-1 h-100 d-flex flex-column overflow-hidden p-3 ${mobileActiveView === 'checkout' ? 'd-none d-md-flex' : 'd-flex'}`}>
          {/* Mobile Top Header */}
          <div className="d-md-none d-flex align-items-center justify-content-between mb-2 flex-shrink-0">
            <span className="fw-bold fs-6 text-dark">POS Billing</span>
            <button
              type="button"
              className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-1"
              onClick={() => setMobileActiveView('checkout')}
            >
              <FontAwesomeIcon icon={faShoppingCart} /> Cart / Checkout
              {cart?.lineItems?.length > 0 && (
                <span className="badge bg-danger rounded-pill ms-1">{cart.lineItems.length}</span>
              )}
            </button>
          </div>

          <div className="flex-grow-1 d-flex flex-column h-100 overflow-hidden">
            <SearchAndModel />
          </div>
          <TodaySalesSummary modal={modal} setModal={setModal} modalId="todaySummaryModalDesktop" />

          {/* Mobile Bottom Cart Bar when items in cart */}
          {cart?.lineItems?.length > 0 && (
            <div className="pos-mobile-cart-bar d-md-none flex-shrink-0 mt-2 p-2 px-3 bg-white border border-light-subtle rounded shadow-sm d-flex align-items-center justify-content-between">
              <div>
                <span className="fw-bold text-dark small">{cart.lineItems.length} {cart.lineItems.length === 1 ? 'item' : 'items'}</span>
                <span className="mx-2 text-muted">•</span>
                <span className="fw-bold text-primary">{doAmountFormat(finalTotal)}</span>
              </div>
              <button
                type="button"
                className="btn btn-primary btn-sm fw-semibold px-3 py-1 rounded"
                onClick={() => setMobileActiveView('checkout')}
              >
                Checkout &rarr;
              </button>
            </div>
          )}
        </div>

        <div className={`pos-sales-checkout-sidebar bg-white border-start p-3 h-100 overflow-auto ${mobileActiveView === 'catalog' ? 'd-none d-md-block' : 'd-block'}`}>
          {/* Mobile Back Button to Catalog */}
          <div className="d-md-none d-flex align-items-center justify-content-between pb-2 mb-2 border-bottom border-light-subtle flex-shrink-0">
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-1"
              onClick={() => setMobileActiveView('catalog')}
            >
              <FontAwesomeIcon icon={faArrowLeft} /> Back to Products
            </button>
            <span className="fw-bold fs-6 text-dark">Checkout</span>
            <span className="badge bg-primary rounded-pill px-2 py-1">{cart?.lineItems?.length || 0}</span>
          </div>

          <div className="pos-right-sidebar-panel d-flex flex-column h-100 flex-grow-1">
            {/* Top Customer Bar */}
            <div className="pos-customer-top-bar d-flex align-items-center gap-2 mb-3 w-100 flex-shrink-0">
              <div className="pos-customer-slot flex-grow-1 min-w-0 d-flex align-items-center">
                {(cart?.customer?.id || cart?.customer?._id) && !isCustomerSearchOpen ? (
                  /* Customer selected: Name and mobile number design matching Image 1 */
                  <div
                    className="pos-customer-card d-flex align-items-center justify-content-between gap-2 p-2 rounded cursor-pointer w-100"
                    onClick={() => setIsCustomerSearchOpen(true)}
                    title="Click to search / change customer"
                  >
                    <div className="pos-customer-card-left d-flex align-items-center gap-2 min-w-0 flex-grow-1 overflow-hidden">
                      <div className="pos-customer-avatar rounded-circle d-flex align-items-center justify-content-center flex-shrink-0">
                        <FontAwesomeIcon icon={faUser} />
                      </div>
                      <div className="pos-customer-info d-flex flex-column justify-content-center min-w-0 flex-grow-1 overflow-hidden">
                        <span className="pos-customer-name text-truncate fw-bold text-dark small">
                          {cart.customer.full_name ||
                            cart.customer.name ||
                            `${cart.customer.first_name || ''} ${cart.customer.last_name || ''}`.trim() ||
                            'Customer'}
                        </span>
                        <span className="pos-customer-details text-truncate text-muted">
                          {cart.customer.mobile_number || cart.customer.phone || 'Customer details'}
                        </span>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="pos-customer-clear-btn btn btn-link text-muted p-1 border-0 d-flex align-items-center justify-content-center flex-shrink-0"
                      title="Clear customer"
                      onClick={(e) => {
                        e.stopPropagation();
                        dispatch(setCustomer({}));
                        setIsCustomerSearchOpen(false);
                      }}
                    >
                      <FontAwesomeIcon icon={faXmark} />
                    </button>
                  </div>
                ) : (
                  /* Customer Search Input using standard SearchField (Image 3) */
                  <SearchField
                    name="q"
                    id="customerSearchField"
                    type="search"
                    autoComplete="off"
                    className="mb-0 w-100"
                    placeholder={t('customer.search_customer') || 'Search customer...'}
                    value={customerSearchQuery}
                    autoFocus={isCustomerSearchOpen}
                    onClick={() => {
                      if (!isCustomerSearchOpen) setIsCustomerSearchOpen(true);
                    }}
                    onFocus={() => {
                      if (!isCustomerSearchOpen) setIsCustomerSearchOpen(true);
                    }}
                    onChange={handleCustomerSearchChange}
                    onKeyDown={handleCustomerKeyDown}
                  />
                )}
              </div>

              {/* Add Customer Button */}
              <Button
                type="button"
                className="light border border-light-subtle rounded flex-shrink-0 d-flex align-items-center justify-content-center pos-customer-add-btn"
                style={{ width: '40px', minWidth: '40px', height: '40px', padding: 0 }}
                data-bs-toggle="modal"
                data-bs-target="#customer-modal-sales"
              >
                <FontAwesomeIcon icon={faPlus} className="me-0" />
              </Button>

              {/* More Actions Menu */}
              {renderMoreMenu()}
            </div>

            {/* Body Area: Customer Tab OR Cart / Checkout */}
            {isCustomerSearchOpen ? (
              /* Customer Tab: List of Customers (Image 2 design, default empty) */
              <div className="customer-list flex-grow-1 overflow-auto">
                {isCustomerSearching ? (
                  <div className="text-center py-4 text-muted">
                    <div className="spinner-border spinner-border-sm text-primary mb-2" role="status"></div>
                    <p className="mb-0" style={{ fontSize: '13px' }}>Searching customers...</p>
                  </div>
                ) : customerSearchResults.length > 0 ? (
                  <ul className="list-group">
                    {customerSearchResults.map((data: any, i: number) => {
                      const makeAvatar = (firstName: string, lastName: string) => {
                        const capitalize = (name: string) =>
                          name?.charAt(0)?.toUpperCase() + name?.slice(1)?.toLowerCase();
                        const firstInitial = capitalize(firstName || '')[0] || '';
                        const lastInitial = capitalize(lastName || '')[0] || '';
                        return `${firstInitial}${lastInitial}`;
                      };

                      const avatar = makeAvatar(data?.first_name, data?.last_name);

                      return (
                        <li
                          key={i}
                          className={`list-group-item ${selectedCustomerIndex === i ? 'active' : ''}`}
                          aria-current="true"
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleSelectCustomer(data)}
                        >
                          <div className="d-flex">
                            <div className="rounded-circle bg-light my-auto avtar">
                              <span className="mb-0">{avatar}</span>
                            </div>
                            <aside className="ms-2">
                              <span>{data?.full_name || data.mobile_number}</span>
                              <span className="m-0 d-block">{data.mobile_number}</span>
                            </aside>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                ) : customerSearchQuery.trim() ? (
                  <div className="text-center py-4">
                    <p className="text-danger mb-0" style={{ fontSize: '13px' }}>{strings.noRecordFound}</p>
                  </div>
                ) : null}
              </div>
            ) : (!cart?.lineItems || cart.lineItems.length === 0) ? (
              /* Image 1: Cart is empty */
              <div className="pos-empty-cart-view d-flex flex-column align-items-center justify-content-center text-center p-4 flex-grow-1 user-select-none">
                <div className="pos-empty-cart-icon-wrap mb-2">
                  <svg width="68" height="68" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="8" cy="21" r="1"></circle>
                    <circle cx="19" cy="21" r="1"></circle>
                    <path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12"></path>
                  </svg>
                </div>
                <h5 className="pos-empty-cart-title fw-bold text-dark mb-1">Cart is empty</h5>
                <p className="pos-empty-cart-subtitle text-muted small mb-0">
                  Scan a barcode or search for a product to start a sale.
                </p>
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm mt-3 d-md-none"
                  onClick={() => setMobileActiveView('catalog')}
                >
                  Browse Products
                </button>
              </div>
            ) : (
              /* Image 2: Cart Items List */
              <div className="pos-active-cart-wrapper d-flex flex-column flex-grow-1 overflow-hidden w-100">
                <div className="pos-cart-header d-flex align-items-center justify-content-between pb-2 mb-2 border-bottom border-light-subtle flex-shrink-0">
                  <h4 className="pos-cart-title fs-6 fw-bold text-dark m-0">Cart ({totalItemCount} items)</h4>
                  <button
                    type="button"
                    className="pos-cart-clear-btn btn btn-link text-danger text-decoration-none p-0 fw-semibold small d-inline-flex align-items-center gap-1"
                    onClick={() => dispatch(addToCart([]))}
                  >
                    <FontAwesomeIcon icon={faTrash} className="me-1" /> Clear
                  </button>
                </div>

                <div className="pos-cart-items-list d-flex flex-column flex-grow-1 overflow-auto">
                  {cart.lineItems.map((item: any, index: number) => {
                    const getInitials = (name: string) => {
                      if (!name) return 'PR';
                      const parts = name.trim().split(/\s+/);
                      if (parts.length >= 2) {
                        return (parts[0][0] + parts[1][0]).toUpperCase();
                      }
                      return name.slice(0, 2).toUpperCase();
                    };

                    const comparePrice =
                      item.regular_price ||
                      item.compare_price ||
                      item.mrp;
                    const hasCrossPrice = Boolean(comparePrice && Number(comparePrice) > Number(item.price));

                    return (
                      <div key={item.id || index} className="pos-cart-item-row d-flex align-items-start gap-2 py-2 border-bottom border-light-subtle w-100">
                        <div className="pos-item-thumbnail rounded overflow-hidden flex-shrink-0 d-flex align-items-center justify-content-center bg-light">
                          {item.image ? (
                            <img src={item.image} alt={item.name} className="pos-item-img w-100 h-100" style={{ objectFit: 'cover' }} />
                          ) : (
                            <div className="pos-item-initials-badge w-100 h-100 d-flex align-items-center justify-content-center fw-bold text-muted small">
                              {item.initials || getInitials(item.name)}
                            </div>
                          )}
                        </div>

                        <div className="flex-grow-1 min-w-0 d-flex flex-column gap-1">
                          <div className="w-100">
                            <div className="pos-item-name fw-semibold text-dark small lh-sm" title={item.name}>
                              {capitalizeFirstLetter(item.name)}
                            </div>

                            <div className="d-flex align-items-center gap-1 mt-1 text-muted" style={{ fontSize: '11.5px' }}>
                              <span className="pos-item-sku">SKU: {item.sku || 'N/A'}</span>
                              <span className="opacity-50">•</span>
                              {hasCrossPrice && (
                                <del className="pos-item-cross-price text-muted" style={{ fontSize: '11px' }}>
                                  {doAmountFormat(comparePrice)}
                                </del>
                              )}
                              <span className="pos-item-unit-price fw-bold text-dark">
                                {doAmountFormat(item.price)}
                              </span>
                            </div>
                          </div>

                          <div className="d-flex align-items-center justify-content-between w-100">
                            <div className="d-flex align-items-center gap-2">
                              <div className="pos-item-qty-stepper d-inline-flex align-items-center border border-light-subtle bg-white overflow-hidden flex-shrink-0">
                                <button
                                  type="button"
                                  className="pos-stepper-btn border-0 text-primary fw-bold px-2"
                                  onClick={() => dispatch(decrementQuantity(item.id))}
                                >
                                  -
                                </button>

                                <span className="pos-stepper-qty fw-bold text-dark px-1 small">{item.quantity}</span>
                                <button
                                  type="button"
                                  className="pos-stepper-btn border-0 text-primary fw-bold px-2"
                                  onClick={() => dispatch(incrementQuantity(item.id))}
                                >
                                  +
                                </button>
                              </div>

                              <div className="pos-item-total-price fw-bold text-dark small text-end">
                                {doAmountFormat(item.price * item.quantity)}
                              </div>
                            </div>

                            <div className="d-flex align-items-center gap-2">
                              <button
                                type="button"
                                className="pos-item-trash-btn btn btn-link text-danger p-1 border-0 d-flex align-items-center justify-content-center flex-shrink-0"
                                onClick={() => dispatch(removeCartItem(index))}
                                title="Remove item"
                              >
                                <FontAwesomeIcon icon={faTrash} />
                              </button>
                            </div>
                          </div>
                        </div>

                      </div>
                    );
                  })}
                </div>

                {/* Checkout & Payment Section below items */}
                <div className="pos-checkout-sidebar-content mt-auto flex-shrink-0 bg-white">
                  <form onSubmit={orderFormik.handleSubmit}>
                    {/* Top Quick Actions Bar */}
                    <div className="pos-quick-actions-bar position-relative d-flex align-items-center gap-2 py-2 my-2 border-bottom border-light-subtle">
                      <div className="pos-note-wrapper position-relative" ref={notePopoverRef}>
                        <button
                          type="button"
                          className={`pos-action-chip pos-action-note btn btn-sm d-inline-flex align-items-center gap-1 rounded fw-semibold ${showNotePanel || orderFormik.values.order_note ? 'active' : ''}`}
                          onClick={() => setShowNotePanel(!showNotePanel)}
                        >
                          <FontAwesomeIcon icon={faFileLines} className="pos-chip-icon" /> Note
                          {Boolean(orderFormik.values.order_note) && (
                            <span className="badge bg-primary rounded-circle p-1 ms-1" style={{ width: '6px', height: '6px', display: 'inline-block' }} />
                          )}
                        </button>

                        {/* Note Popover anchored directly to Note button */}
                        {showNotePanel && (
                          <div className="pos-note-popover position-absolute bg-white border border-light-subtle rounded shadow">
                            <div className="pos-note-popover-header d-flex align-items-center justify-content-between mb-2">
                              <span className="pos-note-title fw-bold small text-dark d-flex align-items-center gap-1">
                                <FontAwesomeIcon icon={faFileLines} className="text-primary small" /> Add note to this order
                              </span>
                              <button
                                type="button"
                                className="pos-note-close-btn btn btn-link text-muted p-0 border-0"
                                onClick={() => setShowNotePanel(false)}
                                title="Close note"
                              >
                                <FontAwesomeIcon icon={faXmark} />
                              </button>
                            </div>

                            <TextArea
                              name="order_note"
                              placeholder="Enter here"
                              rows={2}
                              className="mb-1"
                              value={orderFormik.values.order_note || ''}
                              onChange={(e: any) => orderFormik.setFieldValue('order_note', e.target.value.slice(0, 300))}
                            />

                            <div className="pos-note-footer d-flex align-items-center justify-content-end gap-2">
                              <button
                                type="button"
                                className="pos-btn-cancel btn btn-sm d-inline-flex align-items-center justify-content-center"
                                onClick={() => setShowNotePanel(false)}
                                title="Cancel"
                                aria-label="Cancel"
                              >
                                <FontAwesomeIcon icon={faXmark} />
                              </button>
                              <button
                                type="button"
                                className="pos-btn-save btn btn-sm d-inline-flex align-items-center justify-content-center"
                                onClick={() => setShowNotePanel(false)}
                                title="Save Note"
                                aria-label="Save Note"
                              >
                                <FontAwesomeIcon icon={faCheck} />
                              </button>
                            </div>
                          </div>
                        )}
                      </div>

                      <button
                        type="button"
                        className="pos-action-chip pos-action-discount btn btn-sm d-inline-flex align-items-center gap-1 rounded fw-semibold"
                        onClick={() => {
                          const input = document.getElementById('posDiscountInput');
                          input?.focus();
                        }}
                      >
                        <FontAwesomeIcon icon={faPercent} className="pos-chip-icon" /> Discount
                      </button>
                    </div>

                    <div className="pos-summary-section d-flex flex-column gap-1">
                      <div className="pos-summary-row d-flex align-items-center justify-content-between">
                        <span className="pos-summary-label text-secondary small fw-medium">Items ({totalItemCount})</span>
                        <span className="pos-summary-value text-dark fw-bold small">{doAmountFormat(subTotal)}</span>
                      </div>

                      <div className="pos-summary-row d-flex align-items-center justify-content-between">
                        <span className="pos-summary-label text-secondary small fw-medium">Discount</span>
                        <div className="d-flex align-items-center gap-2">
                          <div className="pos-discount-box d-inline-flex align-items-center border border-light-subtle rounded bg-white px-2">
                            <span className="pos-currency-symbol text-muted small me-1">{currencySymbol}</span>
                            <input
                              id="posDiscountInput"
                              type="number"
                              min="0"
                              value={discountValue}
                              onChange={(e) => {
                                setDiscountValue(Number(e.target.value) || 0);
                                setActiveDiscountPercent(null);
                              }}
                              onFocus={(e) => e.target.select()}
                              className="pos-discount-input-field border-0 bg-transparent fw-bold text-dark w-100"
                            />
                          </div>
                          <button
                            type="button"
                            className={`pos-discount-percent-btn pos-percent-btn btn btn-sm rounded fw-semibold ${activeDiscountPercent === 5 ? 'active' : ''}`}
                            onClick={() => {
                              if (activeDiscountPercent === 5) {
                                setActiveDiscountPercent(null);
                                setDiscountValue(0);
                              } else {
                                setActiveDiscountPercent(5);
                                setDiscountValue(Number((subTotal * 0.05).toFixed(2)));
                              }
                            }}
                          >
                            5%
                          </button>
                          <button
                            type="button"
                            className={`pos-discount-percent-btn pos-percent-btn btn btn-sm rounded fw-semibold ${activeDiscountPercent === 10 ? 'active' : ''}`}
                            onClick={() => {
                              if (activeDiscountPercent === 10) {
                                setActiveDiscountPercent(null);
                                setDiscountValue(0);
                              } else {
                                setActiveDiscountPercent(10);
                                setDiscountValue(Number((subTotal * 0.10).toFixed(2)));
                              }
                            }}
                          >
                            10%
                          </button>
                        </div>
                      </div>

                      <div className="pos-summary-row d-flex align-items-center justify-content-between">
                        <span className="pos-summary-label text-secondary small fw-medium">Tax ({taxRatePercentage}%)</span>
                        <span className="pos-summary-value text-dark fw-bold small">{doAmountFormat(taxAmount)}</span>
                      </div>

                      <div className="pos-summary-row pos-total-row d-flex align-items-center justify-content-between pt-1 mt-1">
                        <span className="pos-total-label fs-5 fw-bolder text-dark">Total</span>
                        <span className="pos-total-value fs-4 fw-bolder text-dark">{doAmountFormat(finalTotal)}</span>
                      </div>
                    </div>

                    {/* Payment Mode Tabs (Image 1) */}
                    <div className="pos-payment-modes-grid d-flex gap-2 w-100 mt-1">
                      {(['cash', 'upi', 'card'] as const).map((mode) => {
                        const labels: Record<string, string> = {
                          cash: 'Cash',
                          upi: 'UPI',
                          card: 'Card',
                          split: 'Split'
                        };
                        const isActive = activePaymentMode === mode && !showNotePanel;
                        return (
                          <button
                            key={mode}
                            type="button"
                            className={`pos-payment-mode-tab flex-grow-1 border-0 rounded fw-semibold small ${isActive ? 'active' : ''}`}
                            onClick={() => handleSelectMode(mode)}
                          >
                            {labels[mode]}
                          </button>
                        );
                      })}
                    </div>

                    {activePaymentMode === 'cash' ? (
                      /* 1. Cash Payment Panel */
                      <div className="pos-payment-panel mt-2 p-3 bg-white border border-light-subtle rounded">
                        <div className="mb-2">
                          <label className="pos-field-label fw-semibold small text-secondary mb-1 d-block">Amount received</label>
                          <div className="pos-cash-tender-row d-flex align-items-center gap-2 flex-wrap mb-2">
                            <div className="pos-cash-input-box d-inline-flex align-items-center border border-light-subtle rounded bg-white px-2">
                              <span className="pos-currency-symbol text-muted small me-1">{currencySymbol}</span>
                              <input
                                type="number"
                                min="0"
                                value={cashReceived}
                                onChange={(e) => setCashReceived(e.target.value)}
                                className="pos-cash-input-field border-0 bg-transparent fw-bold text-dark w-100"
                              />
                            </div>

                            <div className="pos-chips-group d-flex align-items-center gap-2 flex-wrap">
                              <button
                                type="button"
                                className="pos-denom-pill btn btn-light btn-sm fw-semibold text-primary rounded px-2"
                                onClick={() => setCashReceived(finalTotal)}
                              >
                                Exact
                              </button>
                              {dynamicDenominations.map((denom) => (
                                <button
                                  key={denom}
                                  type="button"
                                  className="pos-denom-pill btn btn-light btn-sm fw-semibold text-primary rounded px-2"
                                  onClick={() => setCashReceived(denom)}
                                >
                                  {denom}
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>

                        <div className="pos-change-row d-flex align-items-center justify-content-between pt-2 mt-2 border-top border-light-subtle">
                          <span className="pos-change-label text-secondary small fw-medium">Change to return</span>
                          <span className="pos-change-amount text-success fw-bold fs-6">{doAmountFormat(changeToReturn)}</span>
                        </div>

                        <div className="pos-checkout-btn-wrap mt-3">
                          <button
                            type="submit"
                            className="pos-complete-payment-btn btn btn-primary w-100 d-flex align-items-center justify-content-between fw-bold py-2 px-3 rounded"
                            disabled={isPlaceOrderDisabled}
                            title={placeOrderButtonTitle}
                            onClick={() => {
                              orderFormik.setFieldValue('payment_mode', 'cash');
                              if (cart?.discount) orderFormik.setFieldValue('discount', cart.discount);
                            }}
                          >
                            <span></span>
                            <span className="pos-pay-btn-label">Place Order</span>
                            <span className="pos-pay-btn-hotkey">
                              <FontAwesomeIcon icon={faPrint} className="me-1" /> F7
                            </span>
                          </button>
                        </div>
                      </div>
                    ) : activePaymentMode === 'upi' ? (
                      /* 2. UPI Payment Panel */
                      <div className="pos-payment-panel mt-2 p-3 bg-white border border-light-subtle rounded">
                        <div className="row g-2 mb-2">
                          <div className="col-6">
                            <div className="pos-field-group mb-0">
                              <label className="pos-field-label fw-semibold small text-secondary mb-1 d-block">Amount received</label>
                              <div className="pos-amount-received-box d-flex align-items-center border border-light-subtle rounded bg-white px-2 w-100">
                                <span className="pos-currency-symbol text-muted small me-1">{currencySymbol}</span>
                                <input
                                  type="number"
                                  min="0"
                                  value={cashReceived}
                                  onChange={(e) => setCashReceived(e.target.value)}
                                  className="pos-cash-input-field border-0 bg-transparent fw-bold text-dark w-100"
                                />
                              </div>
                            </div>
                          </div>

                          <div className="col-6">
                            <div className="pos-field-group mb-0">
                              <label className="pos-field-label fw-semibold small text-secondary mb-1 d-block">
                                Reference no. <span className="pos-req-star text-danger">*</span>
                              </label>
                              <input
                                type="text"
                                className="pos-field-input pos-amount-ref-input form-control form-control-sm"
                                placeholder="Enter here"
                                name="payment_ref_number"
                                value={orderFormik.values.payment_ref_number}
                                onChange={orderFormik.handleChange}
                              />
                            </div>
                          </div>
                        </div>

                        <div className="pos-field-group mb-2">
                          <label className="pos-field-label fw-semibold small text-secondary mb-1 d-block">Remark</label>
                          <input
                            type="text"
                            className="pos-field-input form-control form-control-sm"
                            placeholder="Enter remark"
                            value={upiRemark}
                            onChange={(e) => setUpiRemark(e.target.value)}
                          />
                        </div>

                        <div className="pos-checkout-btn-wrap mt-3">
                          <button
                            type="submit"
                            className="pos-complete-payment-btn btn btn-primary w-100 d-flex align-items-center justify-content-between fw-bold py-2 px-3 rounded"
                            disabled={isPlaceOrderDisabled}
                            title={placeOrderButtonTitle}
                            onClick={() => {
                              orderFormik.setFieldValue('payment_mode', 'upi');
                              orderFormik.setFieldValue('remark', upiRemark);
                              if (cart?.discount) orderFormik.setFieldValue('discount', cart.discount);
                            }}
                          >
                            <span></span>
                            <span className="pos-pay-btn-label">Place Order</span>
                            <span className="pos-pay-btn-hotkey">
                              <FontAwesomeIcon icon={faPrint} className="me-1" /> F7
                            </span>
                          </button>
                        </div>
                      </div>
                    ) : activePaymentMode === 'card' ? (
                      /* 3. Card Payment Panel */
                      <div className="pos-payment-panel mt-2 p-3 bg-white border border-light-subtle rounded">
                        <div className="row g-2 mb-2">
                          <div className="col-6">
                            <div className="pos-field-group mb-0">
                              <label className="pos-field-label fw-semibold small text-secondary mb-1 d-block">Amount received</label>
                              <div className="pos-amount-received-box d-flex align-items-center border border-light-subtle rounded bg-white px-2 w-100">
                                <span className="pos-currency-symbol text-muted small me-1">{currencySymbol}</span>
                                <input
                                  type="number"
                                  min="0"
                                  value={cashReceived}
                                  onChange={(e) => setCashReceived(e.target.value)}
                                  className="pos-cash-input-field border-0 bg-transparent fw-bold text-dark w-100"
                                />
                              </div>
                            </div>
                          </div>
                          
                          <div className="col-6">
                            <div className="pos-field-group mb-0">
                              <label className="pos-field-label fw-semibold small text-secondary mb-1 d-block">
                                Reference no. <span className="pos-req-star text-danger">*</span>
                              </label>
                              <input
                                type="text"
                                className="pos-field-input pos-amount-ref-input form-control form-control-sm"
                                placeholder="Enter here"
                                name="payment_ref_number"
                                value={orderFormik.values.payment_ref_number}
                                onChange={orderFormik.handleChange}
                              />
                            </div>
                          </div>
                        </div>

                        <div className="pos-field-group mb-2">
                          <label className="pos-field-label fw-semibold small text-secondary mb-1 d-block">Card type</label>
                          <div className="d-flex align-items-center gap-3 pt-1">
                            <div className="form-check mb-0">
                              <input
                                className="form-check-input cursor-pointer"
                                type="radio"
                                name="card_type"
                                id="card-type-debit"
                                value="Debit Card"
                                checked={cardType === 'Debit Card'}
                                onChange={(e) => setCardType(e.target.value)}
                              />
                              <label className="form-check-label small cursor-pointer" htmlFor="card-type-debit">
                                Debit Card
                              </label>
                            </div>
                            <div className="form-check mb-0">
                              <input
                                className="form-check-input cursor-pointer"
                                type="radio"
                                name="card_type"
                                id="card-type-credit"
                                value="Credit Card"
                                checked={cardType === 'Credit Card'}
                                onChange={(e) => setCardType(e.target.value)}
                              />
                              <label className="form-check-label small cursor-pointer" htmlFor="card-type-credit">
                                Credit Card
                              </label>
                            </div>
                          </div>
                        </div>

                        <div className="pos-field-group mb-2">
                          <label className="pos-field-label fw-semibold small text-secondary mb-1 d-block">Remark</label>
                          <input
                            type="text"
                            className="pos-field-input form-control form-control-sm"
                            placeholder="Enter remark"
                            value={cardRemark}
                            onChange={(e) => setCardRemark(e.target.value)}
                          />
                        </div>

                        <div className="pos-checkout-btn-wrap mt-3">
                          <button
                            type="submit"
                            className="pos-complete-payment-btn btn btn-primary w-100 d-flex align-items-center justify-content-between fw-bold py-2 px-3 rounded"
                            disabled={isPlaceOrderDisabled}
                            title={placeOrderButtonTitle}
                            onClick={() => {
                              orderFormik.setFieldValue('payment_mode', 'card');
                              orderFormik.setFieldValue('card_type', cardType);
                              orderFormik.setFieldValue('remark', cardRemark);
                              if (cart?.discount) orderFormik.setFieldValue('discount', cart.discount);
                            }}
                          >
                            <span></span>
                            <span className="pos-pay-btn-label">Place Order</span>
                            <span className="pos-pay-btn-hotkey">
                              <FontAwesomeIcon icon={faPrint} className="me-1" /> F7
                            </span>
                          </button>
                        </div>
                      </div>
                    ) : null}

                    {/* 4. Split Payment Panel (commented out)
                    activePaymentMode === 'split' ? (
                      <div className="pos-payment-panel mt-2 p-3 bg-white border border-light-subtle rounded">
                        <div className="pos-split-list d-flex flex-column gap-2 mb-2">
                          {splitPayments.map((item, idx) => (
                            <div key={item.id} className="pos-split-row d-flex align-items-center gap-2">
                              <span className="pos-split-method d-flex align-items-center gap-1 fw-bold small text-dark" style={{ width: '70px' }}>
                                {item.method === 'Cash' && <FontAwesomeIcon icon={faMoneyBillWave} />}
                                {item.method === 'UPI' && <FontAwesomeIcon icon={faQrcode} />}
                                {item.method === 'Card' && <FontAwesomeIcon icon={faCreditCard} />}
                                {item.method}
                              </span>
                              <div className="pos-split-input-wrap flex-grow-1 d-inline-flex align-items-center border border-light-subtle rounded px-2">
                                <span className="pos-currency-symbol text-muted small me-1">{currencySymbol}</span>
                                <input
                                  type="number"
                                  min="0"
                                  className="pos-split-input border-0 bg-transparent fw-bold text-dark w-100"
                                  value={item.amount}
                                  placeholder="0"
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    setSplitPayments(splitPayments.map((p, i) => i === idx ? { ...p, amount: val } : p));
                                  }}
                                />
                              </div>
                            </div>
                          ))}
                        </div>

                        <div className="pos-checkout-btn-wrap mt-3">
                          <button
                            type="submit"
                            className="pos-complete-payment-btn btn btn-primary w-100 d-flex align-items-center justify-content-between fw-bold py-2 px-3 rounded"
                            disabled={isPlaceOrderDisabled}
                            title={placeOrderButtonTitle}
                            onClick={() => {
                              orderFormik.setFieldValue('payment_mode', 'split');
                              if (cart?.discount) orderFormik.setFieldValue('discount', cart.discount);
                            }}
                          >
                            <span></span>
                            <span className="pos-pay-btn-label">Place Order</span>
                            <span className="pos-pay-btn-hotkey">
                              <FontAwesomeIcon icon={faPrint} className="me-1" /> P1
                            </span>
                          </button>
                        </div>
                      </div>
                    ) : null
                    */}
                  </form>
                  <CartDiscountModal amount={orderFormik.values.amount} />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Hold List Modal */}
      {
        isHoldModalOpen && (
          <div className="pos-hold-modal-overlay position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center p-3" onClick={() => setIsHoldModalOpen(false)}>
            <div className="pos-hold-modal-dialog bg-white rounded shadow-sm w-100 border border-light-subtle d-flex flex-column overflow-hidden" style={{ maxWidth: '500px', maxHeight: '85vh' }} onClick={(e) => e.stopPropagation()}>
              <div className="pos-hold-modal-header d-flex align-items-center justify-content-between p-3 border-bottom border-light-subtle">
                <h5 className="pos-hold-modal-title m-0 fw-bold fs-6 d-flex align-items-center gap-2 text-dark">
                  <FontAwesomeIcon icon={faPause} /> Held Orders ({heldOrders.length})
                </h5>
                <button
                  type="button"
                  className="pos-hold-modal-close btn btn-link text-muted p-1 border-0"
                  onClick={() => setIsHoldModalOpen(false)}
                >
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              </div>

              <div className="pos-hold-modal-body p-3 overflow-auto flex-grow-1">
                {cart?.lineItems?.length > 0 && (
                  <button
                    type="button"
                    className="pos-hold-action-btn btn btn-light border-primary border-dashed text-primary w-100 py-2 fw-semibold d-flex align-items-center justify-content-center gap-2 mb-3 rounded"
                    onClick={handleHoldCurrentOrder}
                  >
                    <FontAwesomeIcon icon={faPause} /> Hold Current Cart ({totalItemCount} items • {doAmountFormat(finalTotal)})
                  </button>
                )}

                {heldOrders.length === 0 ? (
                  <div className="pos-held-empty-state text-center py-4 text-muted">
                    <p className="mb-1 fw-bold text-dark">No held orders</p>
                    <small>You can hold active sales and resume them at any time.</small>
                  </div>
                ) : (
                  <div className="pos-held-orders-list d-flex flex-column gap-2">
                    {heldOrders.map((order) => (
                      <div key={order.id} className="pos-held-order-card border rounded p-3 d-flex align-items-center justify-content-between bg-light">
                        <div className="pos-held-order-info d-flex flex-column">
                          <span className="pos-held-customer fw-bold text-dark small">{order.customerName}</span>
                          <span className="pos-held-meta text-muted" style={{ fontSize: '12px' }}>
                            {order.items?.length || order.itemCount} items • {doAmountFormat(order.total)} • Held at {order.timestamp}
                          </span>
                        </div>
                        <div className="pos-held-order-actions d-flex align-items-center gap-2">
                          <button
                            type="button"
                            className="pos-resume-btn btn btn-primary btn-sm fw-semibold"
                            onClick={() => handleResumeOrder(order)}
                          >
                            Resume
                          </button>
                          <button
                            type="button"
                            className="pos-delete-held-btn btn btn-outline-danger btn-sm"
                            onClick={() => handleDeleteHeldOrder(order.id)}
                            title="Delete held order"
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )
      }

      <button
        type="button"
        className="btn btn-primary position-fixed left-0 rounded-circle d-flex align-items-center justify-content-center p-0"
        onClick={() => setModal(true)}
        data-bs-toggle="modal"
        data-bs-target="#todaySummaryModalDesktop"
        style={{ bottom: '16px', width: '45px', height: '45px', zIndex: 1050 }}
        aria-label="Open sales summary"
      >
        <FontAwesomeIcon icon={faCubesStacked} className="m-0" />
      </button>

      <CustomerModal
        modalId="customer-modal-sales"
        value={customerSearchQuery}
        setSearchText={setCustomerSearchQuery}
      />
    </>
  )
}
export default SalesTemplate1